/**
 * useSignalling — SignallingClient refs and callback forwarding ref
 * Phase 4 (PRI-291): extracted from useStreamSession
 *
 * Owns: signallingRef, preConnectSignallingRef, signallingCallbacksRef
 * The actual lifecycle (connect/disconnect/callbacks) stays in the orchestrator since
 * callbacks reference cross-cutting state from WebRTC, metering, narrative, etc.
 */

import { useRef } from 'react'
import type { ISignallingClient, SignallingClientOptions } from '../../lib/stream-signalling'

export interface UseSignallingReturn {
  /** The active stream signalling client (legacy OR public-plane adapter, PRI-496) — populated during startStream, cleared on cleanup */
  signallingRef: React.MutableRefObject<ISignallingClient | null>
  /** The pre-connect SignallingClient — populated during preConnect, transferred on Submit (legacy plane only) */
  preConnectSignallingRef: React.MutableRefObject<ISignallingClient | null>
  /**
   * Forwarding callbacks ref: pre-connect SignallingClient delegates to these.
   * Starts as no-ops; the fast path (startStream) populates real handlers before sending initial_prompt.
   * All fields nullable so initial value can be null.
   */
  signallingCallbacksRef: React.MutableRefObject<
    Partial<SignallingClientOptions> & { onMetadata: NonNullable<SignallingClientOptions['onMetadata']> }
  >
}

export function useSignalling(): UseSignallingReturn {
  const signallingRef = useRef<ISignallingClient | null>(null)
  const preConnectSignallingRef = useRef<ISignallingClient | null>(null)
  const signallingCallbacksRef = useRef<
    Partial<SignallingClientOptions> & { onMetadata: NonNullable<SignallingClientOptions['onMetadata']> }
  >({
    onMetadata: () => {},
  })

  return {
    signallingRef,
    preConnectSignallingRef,
    signallingCallbacksRef,
  }
}
